/**
 * The Karting Events Bulgaria 2026 endurance regulations, as data.
 *
 * Every number here comes from `REGULATIONS 2026 V2 (July)`; the rule number in
 * each comment is the clause it encodes, so a rule change is a one-line edit
 * traceable back to the PDF. Nothing in this module touches React, the clock or
 * the network — it is a pure lookup so the planner can be tested exhaustively.
 *
 * These live only on the frontend. The backend stores a plan as opaque jsonb and
 * validates its *shape*, never its legality, exactly as `pit-board.ts` already
 * does — which keeps ~60 regulation constants from being mirrored by hand across
 * two packages that share no module.
 */

export type RaceDuration = 4 | 12 | 25;
export type RaceCategory = 'SILVER' | 'GOLD' | 'PRO';
export type TrackId = 'haskovo' | 'kyustendil' | 'vratsa' | 'serres' | 'slovakia';

/** Where the long stints sit. See `DerivedConstraints.planningFloorMinutes`. */
export type StintBias = 'even' | 'front' | 'back';

/**
 * The shortest stint the planner will lay out when the regulations set no
 * minimum of their own — 4-hour races have none (rule 13a), while 12- and
 * 25-hour races are held to ten minutes by rule 13b.
 *
 * One minute is about a single lap: out of the pit lane, round, and straight
 * back in. That is a real tactic rather than an oversight — a 4-hour race must
 * still make 8 to 11 stops, and discharging one cheaply late buys longer stints
 * while the track is worth having. The planner allows what the rules allow and
 * lets the sheet show the consequence; a floor here would be an invented rule.
 */
export const PLANNING_FLOOR_MINUTES = 1;

/**
 * How much clear air the planner leaves either side of a pit lane closure.
 *
 * The regulation boundary is exact on paper and vague in practice: a closure
 * runs from when the cones go out or the message is posted, the midpoint one
 * only lifts five minutes after the leader crosses the line, and a safety kart
 * can shut the lane with no warning at all (rule 16c). Planning a stop onto the
 * edge of that is betting three laps on the timing being to the second.
 *
 * This is prudence, not regulation, so it shapes what the generator *plans* and
 * never what the validator calls illegal — a stop inside the margin but outside
 * the real closure is a warning. Per-strategy, because how much room a team
 * wants depends on the track: one lap of Haskovo is not one lap of Slovakia.
 */
export const PIT_MARGIN_MINUTES = 5;

/** A margin wider than this would swallow the race it is protecting. */
export const MAX_PIT_MARGIN_MINUTES = 30;

/**
 * How concentrated the closing stops may get before the plan is worth a warning,
 * as a multiple of an even plan's share of the final quarter.
 *
 * Raw slack is the wrong measure here: ten stops in four hours always puts the
 * last one near the end, so every legal plan looks tight and the signal is
 * useless. What separates a sound plan from a fragile one is how many stops are
 * still *outstanding* late on. Rule 16c shuts the lane with no warning and keeps
 * it shut for five minutes after the safety kart returns, and a queue in the
 * kart-change area is expressly the team's own risk (rule 22b ii) — so each
 * outstanding stop is a separate chance to owe 5 laps under rule 20j.
 *
 * Twice the even share is a plan that has deferred half its closing work.
 */
export const LATE_STOP_CONCENTRATION = 2;

export const RACE_DURATIONS: RaceDuration[] = [4, 12, 25];
export const RACE_CATEGORIES: RaceCategory[] = ['SILVER', 'GOLD', 'PRO'];

export interface TrackInfo {
  id: TrackId;
  name: string;
  /** Rule 12b — minimum time in the pit lane per stop, during the race. */
  minPitSeconds: number;
}

/** Rule 12b. Qualifying has no minimum, only a walking-speed requirement. */
export const TRACKS: Record<TrackId, TrackInfo> = {
  haskovo: { id: 'haskovo', name: 'Haskovo', minPitSeconds: 135 },
  kyustendil: { id: 'kyustendil', name: 'Kyustendil', minPitSeconds: 135 },
  vratsa: { id: 'vratsa', name: 'Vratsa', minPitSeconds: 150 },
  serres: { id: 'serres', name: 'Serres (Greece)', minPitSeconds: 150 },
  slovakia: { id: 'slovakia', name: 'Slovak Karting Center', minPitSeconds: 165 },
};

export const TRACK_IDS = Object.keys(TRACKS) as TrackId[];

export interface CalendarEntry {
  label: string;
  trackId: TrackId;
  /** "12+4" weekends run a 12h and a 4h race — two plans, not one. */
  durations: RaceDuration[];
}

/** Rule 2 — the 2026 season, used to seed the pickers. */
export const CALENDAR_2026: CalendarEntry[] = [
  { label: 'EKO 12+4 Hours of Bulgaria · Apr 18–19', trackId: 'haskovo', durations: [12, 4] },
  { label: 'EKO 12+4 Hours of Bulgaria (2) · May 16–17', trackId: 'haskovo', durations: [12, 4] },
  { label: 'EKO 25 Hours of Bulgaria · Jun 20–21', trackId: 'kyustendil', durations: [25] },
  { label: 'EKO 12+4 Hours of Bulgaria (3) · Aug 29–30', trackId: 'kyustendil', durations: [12, 4] },
  { label: '25 Hours of Slovakia · Sep 26–27', trackId: 'slovakia', durations: [25] },
  { label: 'EKO 12+4 Hours of Bulgaria (4) · Oct 24–25', trackId: 'vratsa', durations: [12, 4] },
];

/** Rule 10 — mandatory stops. Rule 20j penalises each one short by 5 laps. */
const REQUIRED_STOPS: Record<RaceDuration, Record<RaceCategory, number>> = {
  4: { PRO: 8, GOLD: 10, SILVER: 11 },
  12: { PRO: 17, GOLD: 19, SILVER: 20 },
  25: { PRO: 30, GOLD: 33, SILVER: 35 },
};

/** Rule 13 — shortest legal stint, measured Pit Out → Pit In. */
const MIN_STINT: Record<RaceDuration, number> = { 4: 0, 12: 10, 25: 10 };

/** Rule 14 — longest legal stint. Not category-dependent. */
const MAX_STINT: Record<RaceDuration, number> = { 4: 40, 12: 60, 25: 70 };

/** Rule 15 — the first stint of the race is capped tighter, by category. */
const MAX_FIRST_STINT: Record<RaceDuration, Record<RaceCategory, number>> = {
  4: { SILVER: 30, GOLD: 35, PRO: 40 },
  12: { SILVER: 40, GOLD: 50, PRO: 60 },
  25: { SILVER: 50, GOLD: 60, PRO: 70 },
};

/** Rule 6 — squad size. */
const DRIVER_COUNT: Record<RaceDuration, { min: number; max: number }> = {
  4: { min: 2, max: 5 },
  12: { min: 2, max: 7 },
  25: { min: 2, max: 9 },
};

/**
 * Rule 17 — minimum total driving time per driver, in minutes, keyed by squad
 * size. A smaller squad owes each driver more. 4h has no minimum at all.
 */
const MIN_DRIVER_MINUTES: Partial<Record<RaceDuration, Record<number, number>>> = {
  12: { 7: 0, 6: 0, 5: 120, 4: 150, 3: 180, 2: 270 },
  25: { 9: 30, 8: 30, 7: 30, 6: 120, 5: 180, 4: 270, 3: 360, 2: 600 },
};

/** Rule 18 — maximum total driving time per driver. Specified for 4h only. */
const MAX_DRIVER_MINUTES: Partial<Record<RaceDuration, Record<number, number>>> = {
  4: { 5: 60, 4: 75, 3: 105, 2: 150 },
};

/** Rule 16d–g — how long the lane stays shut at each end of the race. */
const EDGE_CLOSURE: Record<RaceDuration, number> = { 4: 10, 12: 15, 25: 15 };

/** Rule 16f–g — half-width of the closure around the direction change. */
const MIDPOINT_CLOSURE: Record<RaceDuration, number> = { 4: 5, 12: 10, 25: 10 };

export interface ClosedPitWindow {
  /** Minutes from the race start. Entering the lane in `[from, to)` is illegal. */
  fromMinute: number;
  toMinute: number;
  reason: string;
  /** Which closure this is, so callers need not match on `reason`. */
  kind: 'start' | 'midpoint' | 'end';
}

export interface DerivedConstraints {
  raceMinutes: number;
  requiredStops: number;
  minPitSeconds: number;
  minPitMinutes: number;
  minStintMinutes: number;
  /** What the generator treats as the shortest stint worth planning. */
  planningFloorMinutes: number;
  maxStintMinutes: number;
  maxFirstStintMinutes: number;
  /**
   * How many stints may sit at the maximum stint time before rule 18's
   * per-driver total becomes unsatisfiable. A driver can hold
   * `floor(maxDriverMinutes / maxStintMinutes)` full-length stints, so the squad
   * can absorb that many each — beyond it, somebody must take two and go over.
   * Null when the format sets no per-driver maximum (everything but 4 hours).
   */
  maxStintsAtCap: number | null;
  minDriverMinutes: number | null;
  maxDriverMinutes: number | null;
  minDrivers: number;
  maxDrivers: number;
  /** The closures as written. Entering one of these is a 3-lap penalty. */
  closedPitWindows: ClosedPitWindow[];
  /**
   * The same closures widened by `PIT_MARGIN_MINUTES` either side — what the
   * generator steers around. Wider than the rules require, on purpose.
   */
  planningPitWindows: ClosedPitWindow[];
  /** The margin actually applied, after defaulting and clamping. */
  pitMarginMinutes: number;
  /** Rule 11b — SILVER changes driver at every stop; PRO/GOLD may not have to. */
  mustChangeDriverEveryStop: boolean;
  /** Rule 11c — PRO/GOLD may double-stint a driver, changing only the kart. */
  maxConsecutiveStints: number;
}

/** The subset of the config `deriveConstraints` actually reads. */
export interface ConstraintInputs {
  durationHours: RaceDuration;
  category: RaceCategory;
  trackId: TrackId;
  /** Overrides rule 10 — see the driver-changes/pit-stops ambiguity in the UI. */
  requiredStopsOverride?: number | null;
  /** False when the bulletin declares a single direction (rain): no midpoint shut. */
  directionChange?: boolean;
  /** Clear air kept either side of a closure. Defaults to PIT_MARGIN_MINUTES. */
  pitMarginMinutes?: number;
}

/**
 * Rule 16 — the windows in which entering the pit lane costs 3 laps.
 *
 * The safety-kart closure (16c) is deliberately absent: it is called on the day
 * and cannot be planned around, so the UI shows it as a standing note instead.
 */
export function closedPitWindows(
  durationHours: RaceDuration,
  directionChange = true,
): ClosedPitWindow[] {
  const raceMinutes = durationHours * 60;
  const edge = EDGE_CLOSURE[durationHours];
  const mid = MIDPOINT_CLOSURE[durationHours];

  const windows: ClosedPitWindow[] = [
    {
      fromMinute: 0,
      toMinute: edge,
      reason: `First ${edge} minutes of the race`,
      kind: 'start',
    },
  ];

  if (directionChange) {
    windows.push({
      fromMinute: raceMinutes / 2 - mid,
      toMinute: raceMinutes / 2 + mid,
      reason: `Direction change at the midpoint (±${mid} min)`,
      kind: 'midpoint',
    });
  }

  windows.push({
    fromMinute: raceMinutes - edge,
    toMinute: raceMinutes,
    reason: `Last ${edge} minutes of the race`,
    kind: 'end',
  });

  return windows;
}

/**
 * When the track reverses, in minutes from the start — the midpoint of the race
 * (rule 3e). Null when the bulletin declares a single direction, which is what
 * happens in the rain.
 */
export function directionChangeMinute(
  durationHours: RaceDuration,
  directionChange = true,
): number | null {
  return directionChange ? (durationHours * 60) / 2 : null;
}

/** Every regulation bound that applies to one (race, category, track, squad). */
export function deriveConstraints(
  config: ConstraintInputs,
  driverCount: number,
): DerivedConstraints {
  const { durationHours, category, trackId } = config;
  const counts = DRIVER_COUNT[durationHours];

  // Clamp before the lookup: the tables only cover legal squad sizes, and an
  // out-of-range roster should still get sane bounds rather than `undefined`.
  const clamped = Math.min(Math.max(driverCount, counts.min), counts.max);
  const minPitSeconds = TRACKS[trackId].minPitSeconds;

  const override = config.requiredStopsOverride;
  const requiredStops =
    override === null || override === undefined
      ? REQUIRED_STOPS[durationHours][category]
      : override;

  const windows = closedPitWindows(durationHours, config.directionChange ?? true);
  const margin = Math.max(
    0,
    Math.min(MAX_PIT_MARGIN_MINUTES, config.pitMarginMinutes ?? PIT_MARGIN_MINUTES),
  );
  const minStintMinutes = MIN_STINT[durationHours];
  const maxStintMinutes = MAX_STINT[durationHours];
  const maxDriverMinutes = MAX_DRIVER_MINUTES[durationHours]?.[clamped] ?? null;

  return {
    raceMinutes: durationHours * 60,
    requiredStops,
    minPitSeconds,
    minPitMinutes: minPitSeconds / 60,
    minStintMinutes,
    planningFloorMinutes:
      minStintMinutes > 0 ? minStintMinutes : PLANNING_FLOOR_MINUTES,
    maxStintMinutes,
    maxFirstStintMinutes: MAX_FIRST_STINT[durationHours][category],
    maxStintsAtCap:
      maxDriverMinutes === null || maxStintMinutes <= 0
        ? null
        : clamped * Math.floor(maxDriverMinutes / maxStintMinutes),
    minDriverMinutes: MIN_DRIVER_MINUTES[durationHours]?.[clamped] ?? null,
    maxDriverMinutes,
    minDrivers: counts.min,
    maxDrivers: counts.max,
    closedPitWindows: windows,
    planningPitWindows: withPitMargin(windows, durationHours * 60, margin),
    pitMarginMinutes: margin,
    mustChangeDriverEveryStop: category === 'SILVER',
    maxConsecutiveStints: category === 'SILVER' ? 1 : 2,
  };
}

/**
 * Widen each closure by `margin` either side, clamped to the race.
 *
 * The start closure gains nothing at its front and the end closure nothing at
 * its back — there is no race there to pit in.
 */
export function withPitMargin(
  windows: ClosedPitWindow[],
  raceMinutes: number,
  margin: number = PIT_MARGIN_MINUTES,
): ClosedPitWindow[] {
  return windows.map((w) => ({
    ...w,
    fromMinute: Math.max(0, w.fromMinute - margin),
    toMinute: Math.min(raceMinutes, w.toMinute + margin),
  }));
}

/** True when `minute` falls inside a window where the lane is shut. */
export function isPitClosed(minute: number, windows: ClosedPitWindow[]): boolean {
  return windows.some((w) => minute >= w.fromMinute && minute < w.toMinute);
}

/** The window `minute` falls in, or null when the lane is open. */
export function closureAt(
  minute: number,
  windows: ClosedPitWindow[],
): ClosedPitWindow | null {
  return windows.find((w) => minute >= w.fromMinute && minute < w.toMinute) ?? null;
}
